import {
  chunkTransformations,
  type ChunkTransformationKey,
} from "@faire/localization/react/chunkTransformations";
import { StrictLocalMsg } from "@faire/localization/react/StrictLocalMsg";
import { enumValue, protoEntries } from "@faire/typescript";
import React from "react";

import { getTypedChildren } from "./getTypedChildren";

const POSSIBLE_FORMATTING_PLACEHOLDER_JSX_SUFFIXES = ["Formatting"];
const POSSIBLE_FORMATTING_PLACEHOLDER_JSX_SUFFIXES_PATTERN = new RegExp(
  // oxlint-disable-next-line @faire/no-dollar-symbol-in-strings
  `${POSSIBLE_FORMATTING_PLACEHOLDER_JSX_SUFFIXES.flatMap((suffix) => `(${suffix})`).join("|")}$`
);

/**
 * Get all the children that are instances of `<StrictLocalMsg>` that is using only `allowedChunkTransformationKeys` chunk transformations (if provided).
 */
export const getStrictLocalMsgChildren = (
  children: React.ReactNode,
  allowedChunkTransformationKeys?: ChunkTransformationKey[]
) => {
  const defaultMessagePattern = allowedChunkTransformationKeys?.length
    ? getDefaultMessagePattern(allowedChunkTransformationKeys)
    : undefined;
  const [{ strictLocalMsgs }, otherChildren] = getTypedChildren(children, {
    strictLocalMsgs: [
      StrictLocalMsg,
      (props: React.ComponentProps<typeof StrictLocalMsg>) => {
        if (!defaultMessagePattern) {
          return true;
        }
        const isDefaultMessageValid = defaultMessagePattern.test(
          props.defaultMessage
        );
        const values: Record<string, unknown> = props.values ?? {};
        const areValuesValid = protoEntries(values).every(
          ([placeholderKey, placeholderTransform]) => {
            const placeholderKeyWithoutSuffix = placeholderKey.replace(
              POSSIBLE_FORMATTING_PLACEHOLDER_JSX_SUFFIXES_PATTERN,
              ""
            );
            const isPlaceholderValueValid =
              enumValue(chunkTransformations, placeholderKey) ===
              placeholderTransform;
            const isPlaceholderWithoutSuffixValid =
              enumValue(chunkTransformations, placeholderKeyWithoutSuffix) ===
              placeholderTransform;
            return isPlaceholderValueValid || isPlaceholderWithoutSuffixValid;
          }
        );
        return isDefaultMessageValid && areValuesValid;
      },
    ],
  });
  return [{ strictLocalMsgs: strictLocalMsgs }, otherChildren] as const;
};

const getDefaultMessagePattern = (
  allowedChunkTransformations: ChunkTransformationKey[]
) =>
  new RegExp(
    // oxlint-disable-next-line @faire/no-dollar-symbol-in-strings
    `^([^<>]|${allowedChunkTransformations.flatMap((key) => ["", ...POSSIBLE_FORMATTING_PLACEHOLDER_JSX_SUFFIXES].map((suffix) => [`(<${key}${suffix}>[^<>]*</${key}${suffix}>)`])).join("|")})+$`
  );
